import PageHeading from "../../../components/desktop/PageHeading";
import React, { useState, useEffect } from "react";
import { api } from "../../../utils/api";
import { toast } from "react-toastify";
import { MdDeleteOutline } from "react-icons/md";
import { useAuthStore } from "../../../store/authStore";
import { FaRegEdit } from "react-icons/fa";
import "./Shiftmaster.css";
import back from "../../../assets/back.png";
import { useLoaderStore } from "../../../store/loaderStore";

export default function Shiftmaster() {
  const { showLoader, hideLoader } = useLoaderStore();

  const [shifts, setShifts] = useState([]);

  const { user, isAuthenticated } = useAuthStore();

  const companyId = user?.company_id;

  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);

  const [form, setForm] = useState({
    shift_name: "",
    shift_start_time: "",
    shift_end_time: "",
    is_next_day: false,
    status: true,
  });

  const fetchShifts = async (showToast = false) => {
    try {
      showLoader("master");

      const response = await api.get(`/shift/list/${companyId}`);

      const result = await response.json();

      setShifts(result);

      if (showToast) {
        toast.success("Shifts fetched successfully");
      }
    } catch (error) {
      console.error(error);

      toast.error(error.message || "Failed to load shifts");
    } finally {
      hideLoader();
    }
  };

  useEffect(() => {
    if (companyId && isAuthenticated) {
      fetchShifts(true);
    }
  }, [companyId, isAuthenticated]);

  const toggleStatus = async (item) => {
    try {
      showLoader("master");
      const response = await api.put(`/shift/update/${item.id}`, {
        shift_name: item.shift_name,
        shift_start_time: item.shift_start_time,
        shift_end_time: item.shift_end_time,
        is_next_day: item.is_next_day ? 1 : 0,
        isactive: item.isactive === 1 ? 0 : 1
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Failed to update status");
      }

      await fetchShifts();

      toast.success(
        `Shift ${
          item.isactive === 1 ? "deactivated" : "activated"
        } successfully`,
      );
    } catch (error) {
      console.error(error);
      toast.error(error.message || "Failed to update status");
    } finally {
      hideLoader();
    }
  };

  const handleEdit = async (item) => {
    try {
      showLoader("master");
      const response = await api.get(`/shift/${item.id}`);

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to load shift");
      }

      setEditId(item.id);

      setForm({
        shift_name: data.shift_name || "",
        shift_start_time: data.shift_start_time || "",
        shift_end_time: data.shift_end_time || "",
        is_next_day: data.is_next_day === 1,
        status: data.isactive === 1,
      });

      setShowForm(true);
    } catch (error) {
      console.error(error);
      toast.error(error.message || "Failed to load shift");
    } finally {
      hideLoader();
    }
  };

  const handleSave = async () => {
    if (!form.shift_name || !form.shift_start_time || !form.shift_end_time) {
      toast.warning("Please fill all required fields");
      return;
    }
    if (
    !form.is_next_day &&
    form.shift_end_time <= form.shift_start_time
  ) {
    toast.warning("Shift end time cannot be less than or equal to shift start time");
    return;
  }

    try {
      showLoader("master");
      let response;

      if (editId) {
        response = await api.put(`/shift/update/${editId}`, {
          shift_name: form.shift_name,
          shift_start_time: form.shift_start_time,
          shift_end_time: form.shift_end_time,
          is_next_day: form.is_next_day ? 1 : 0,
          isactive: form.status ? 1 : 0
        });
      } else {
        response = await api.post(`/shift/add`, {
          company_id: companyId,
          shift_name: form.shift_name,
          shift_start_time: form.shift_start_time,
          shift_end_time: form.shift_end_time,
          is_next_day: form.is_next_day ? 1 : 0
        });
      }

      if (!response.ok) {
        throw new Error("Failed to save shift");
      }

      await fetchShifts();

      toast.success(
        editId ? "Shift updated successfully" : "Shift added successfully",
      );

      handleCancel();
    } catch (error) {
      console.error(error);
      toast.error(error.message || "Failed to save shift");
    } finally {
      hideLoader();
    }
  };

  const handleDelete = async (shiftId) => {
    try {
      showLoader("master");
      const response = await api.delete(`/shift/delete/${shiftId}`);

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Failed to delete shift");
      }

      await fetchShifts();

      toast.success("Shift deleted successfully");
    } catch (error) {
      console.error(error);
      toast.error(error.message || "Failed to delete shift");
    } finally {
      hideLoader();
    }
  };
  
  const handleCancel = () => {
    setForm({
      shift_name: "",
      shift_start_time: "",
      shift_end_time: "",
      is_next_day: false,
      status: true,
    });

    setEditId(null);
    setShowForm(false);
  };

  return (
    <div className="shift-master-container">
      <PageHeading>Shift Master</PageHeading>
      {!showForm ? (
        <>
          <div className="shift-master-top">
            <button
              className="add-shift-btn"
              onClick={() => {
                setEditId(null);

                setForm({
                  shift_name: "",
                  shift_start_time: "",
                  shift_end_time: "",
                  is_next_day: false,
                  status: true,
                });

                setShowForm(true);
              }}
            >
              + Add Shift
            </button>
          </div>

          <div className="shift-table-wrapper">
            <table className="shift-master-table">
              <thead>
                <tr>
                  <th className="shift-serial-col">Sr.no</th>
                  <th className="shift-name-col">Shift Name</th>
                  <th className="shift-start-col">Shift Start Time</th>
                  <th className="shift-end-col">Shift End Time</th>
                  <th className="shift-nextday-col">Is Next Day</th>
                  <th className="shift-status-col">Status</th>
                  <th className="shift-action-col">Action</th>
                </tr>
              </thead>

              <tbody>
                {shifts.map((item, index) => (
                  <tr key={item.id}>
                    <td className="shift-serial-col">{index + 1}</td>

                    <td className="shift-name-col">{item.shift_name}</td>

                    <td className="shift-start-col">{item.shift_start_time}</td>
                    <td className="shift-end-col">{item.shift_end_time}</td>

                    <td className="shift-nextday-col">
                      {item.is_next_day ? "Yes" : "No"}
                    </td>

                    <td className="shift-status-col">
                      <label className="shift-switch">
                        <input
                          type="checkbox"
                          checked={item.isactive === 1}
                          onChange={() => toggleStatus(item)}
                        />
                        <span className="shift-slider"></span>
                      </label>
                    </td>

                    <td className="shift-action-col">
                      <FaRegEdit
                        className="shift-edit-icon"
                        onClick={() => handleEdit(item)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <div className="shift-form-container">
          <div className="shift-form-header">
            <img
              src={back}
              alt="Back"
              className="shift-back-btn-img"
              onClick={handleCancel}
            />

            <h3>{editId ? "Edit Shift" : "Add Shift"}</h3>
          </div>

          <div className="shift-form">
            <div className="shift-field-group">
              <label>
                Shift Name <span>*</span>
              </label>
              <input
                type="text"
                value={form.shift_name}
                onChange={(e) =>
                  setForm({
                    ...form,
                    shift_name: e.target.value,
                  })
                }
                placeholder="Enter Shift Name"
              />
            </div>

            <div className="shift-field-group">
              <label>
                Shift Start Time <span>*</span>
              </label>
              <input
                type="time"
                value={form.shift_start_time}
                onChange={(e) =>
                  setForm({
                    ...form,
                    shift_start_time: e.target.value,
                  })
                }
              />
            </div>

            <div className="shift-field-group">
              <label>
                Shift End Time <span>*</span>
              </label>
              <input
                type="time"
                value={form.shift_end_time}
                onChange={(e) =>
                  setForm({
                    ...form,
                    shift_end_time: e.target.value,
                  })
                }
              />
            </div>

            <div className="shift-field-group">
              <label>Is Next Day</label>
              <select
                value={form.is_next_day ? "Yes" : "No"}
                onChange={(e) =>
                  setForm({
                    ...form,
                    is_next_day: e.target.value === "Yes",
                  })
                }
              >
                <option value="No">No</option>
                <option value="Yes">Yes</option>
              </select>
            </div>

            <div className="shift-field-group">
              <label>Status</label>
              <select
                value={form.status ? "Active" : "Inactive"}
                onChange={(e) =>
                  setForm({
                    ...form,
                    status: e.target.value === "Active",
                  })
                }
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
          </div>

          <div className="shift-form-actions">
            <button className="shift-cancel-btn" onClick={handleCancel}>
              Cancel
            </button>

            <button className="shift-save-btn" onClick={handleSave}>
              {editId ? "Update" : "Save"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}